"use client";

import {
  FC,
  memo,
  useEffect,
  useState,
  Suspense,
} from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";

import { useSignupFlowReset } from "apps/website/hooks/useSignupFlowReset";
import DefaultHead from "apps/website/components/page/DefaultHead/DefaultHead";
import { makePageTags } from "apps/website/utils/website/head";
import Header from "apps/website/components/page/Header/Header";
import {
  HEADER_LINKS_NONE,
} from "apps/website/components/page/Header/Header.map";
import {
  SIGNUP_FLOW_PAGES,
  SignupFlowSlug,
} from "libs/state/src/lib/stores/signupFlow/constants";
import { useFlowNavigation, useSignupFlowStore } from "@./state";
import { getInterpolationVariables } from "libs/utils/sign-up/fresh";

import Main from "../components/layout/Main/Main";
import Footer from "../components/page/Footer/Footer";
import { themeRootClassMap } from "../maps/Theme.map";
import cx from "../utils/misc/cx";
import {
  QueryParamHandler,
} from "../components/sign-up/fresh/common/QueryParamHandler/QueryParamHandler";
import {
  SignupLoadingScreen,
} from "../components/sign-up/fresh/common/SignupLoadingScreen/SignupLoadingScreen";
import {
  ProgressSteps,
} from "../components/sign-up/fresh/common/ProgressSteps/ProgressSteps";
import {
  TitleSection,
} from "../components/sign-up/fresh/common/TitleSection/TitleSection";

import { ILayout } from "./layouts.map";

export interface SignupFlowLayoutProps extends Partial<ILayout> {}

const INITIAL_SLUG = SignupFlowSlug.CAT_NAMES;
const LOGIN_LINK = { id: "g-0", name: "Login", to: "/login" };

const FreshSignupFlow: FC<Readonly<SignupFlowLayoutProps>> =
({ children, head, lastBuilt }) => {
  const t = useTranslations("SUFv2.titles");
  const uxT = useTranslations("General.UX");
  const pathname = usePathname();
  const [ hideBackButton, setHideBackButton ] = useState(true);
  const [ mounted, setMounted ] = useState(false);

  const { canGoBack, goBack } = useFlowNavigation(pathname);

  const {
    cats,
    activeCatId,
    isStoreInitialised,
    versionMismatchDetected,
    getActiveCatIds,
  } = useSignupFlowStore((state) => ({
    activeCatId: state.activeCatId,
    cats: state.cats,
    isStoreInitialised: state.isStoreInitialised,
    versionMismatchDetected: state.versionMismatchDetected,
    getActiveCatIds: state.getActiveCatIds,
  }));

  // Synchronously check if we should redirect (prevents rendering with invalid state)
  const shouldRedirect = isStoreInitialised &&
    pathname !== INITIAL_SLUG &&
    (versionMismatchDetected || getActiveCatIds().length === 0);

  const currentCat = cats[activeCatId];
  const { title, hideProgressSteps, hideFooter = true, bgColor = "light-grey", headerBgColor = "light" } = SIGNUP_FLOW_PAGES[pathname as SignupFlowSlug];

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setHideBackButton(pathname === INITIAL_SLUG);
  }, [ pathname ]);

  useSignupFlowReset();

  const getTitle = () => {
    if (title && currentCat) {
      const variables = getInterpolationVariables({ name: currentCat?.name, gender: currentCat?.gender });
      return t(title, variables);
    }
    return t(title) || "";
  };

  return (
    <div>
      <DefaultHead
        seo={makePageTags(head?.seo)}
        /* @ts-ignore */
        hideFromSearch={head?.hide_from_search_engines}
        lastBuilt={lastBuilt}
        fallbackTags={head?.fallbackTags && makePageTags(head.fallbackTags)}
      />
      <Suspense>
        <QueryParamHandler />
      </Suspense>

      <Header
        {...HEADER_LINKS_NONE}
        hasAnnouncementBanner={false}
        guestLinks={hideBackButton ? [ LOGIN_LINK ] : []}
        historyNavigation={{
          back: () => (canGoBack ? goBack() : undefined),
          hide: hideBackButton,
        }}
        backgroundColor={headerBgColor}
      />

      <Main className={cx(themeRootClassMap[bgColor], "min-h-[calc(100dvh-var(--header-height-mobile))] xl:min-h-[calc(100dvh-var(--header-height-desktop))]")}>
        { !mounted || !isStoreInitialised || shouldRedirect ? (
          <SignupLoadingScreen uxT={uxT} />
        ) : (
          <>
            <ProgressSteps
              hideProgressSteps={hideProgressSteps}
              pathname={pathname as SignupFlowSlug}
            />
            <TitleSection
              title={title}
              hideProgressSteps={hideProgressSteps}
              getTitle={getTitle}
            />
            { children }
          </>
        ) }
      </Main>

      { !hideFooter && (
        <Footer />
      ) }
    </div>
  );
};

export default memo(FreshSignupFlow);
